import { SapDunningBlockingReasonCode, SapDunningBlockSyncStatus } from "@/api-schema-enums";
import type { OpenPosition } from "@/common/api/open-position/use-open-position-search.query";
import { EmptyValue } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipProviderIfMissing, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckIcon, ClockIcon, XIcon, type LucideIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { DUNNING_BLOCK_REASON_LABEL_KEYS } from "../_helpers/dunning-block-reasons";

const STATUS_DISPLAY = {
  [SapDunningBlockSyncStatus.PENDING]: { label: "fia:DunningSapSyncPending", icon: ClockIcon },
  [SapDunningBlockSyncStatus.SUCCESS]: {
    label: "fia:DunningSapSyncSuccess",
    icon: CheckIcon,
  },
  [SapDunningBlockSyncStatus.FAILED]: { label: "fia:DunningSapSyncFailed", icon: XIcon },
} satisfies Record<SapDunningBlockSyncStatus, { label: string; icon: LucideIcon }>;

type DunningReasonCellProps = Pick<OpenPosition, "dunningBlock" | "dunningBlockSapSync">;

export function DunningReasonCell({ dunningBlock, dunningBlockSapSync: sync }: DunningReasonCellProps): ReactElement {
  const { t } = useTranslation(["fia"]);
  if (!dunningBlock && !sync) return <EmptyValue />;

  const label = dunningBlock ? t(DUNNING_BLOCK_REASON_LABEL_KEYS[dunningBlock.reason]) : t("fia:NoDunningBlock");
  const display = sync ? STATUS_DISPLAY[sync.status] : undefined;
  const StatusIcon = display?.icon;
  const statusLabel = t(display?.label ?? "fia:DunningSapSyncUntracked");

  return (
    <TooltipProviderIfMissing>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`${label} — ${t("fia:DunningSapSyncTitle")}: ${statusLabel}`}
            className="focus-visible:ring-ring inline-flex max-w-full cursor-help items-center gap-1.5 rounded-sm align-middle focus-visible:ring-2 focus-visible:outline-none"
          >
            {StatusIcon && <StatusIcon className="size-3.5 shrink-0" aria-hidden="true" />}
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-64 space-y-1 text-left whitespace-normal">
            <div className="font-medium">
              {t("fia:DunningSapSyncTitle")}: <span>{statusLabel}</span>
            </div>
            {sync && (
              <div>
                {t("fia:DunningSapSyncRequestedAction")}:{" "}
                {t(
                  sync.requestedReasonCode === SapDunningBlockingReasonCode.Cleared
                    ? "fia:RemoveDunningBlock"
                    : "fia:SetDunningBlock",
                )}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProviderIfMissing>
  );
}
